import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Wise International Consultancy';
const SITE_URL = 'https://wiseintl.edu.np';
const DEFAULT_DESCRIPTION = "Nepal's trusted education consultancy for studying abroad. Expert guidance for MBBS, Engineering, Management admissions in India, Bangladesh, Philippines, China, Russia & more.";
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.jpg`;

/**
 * SEOHead — Reusable SEO component for every page
 * 
 * Usage:
 *   <SEOHead 
 *     title="About Us"
 *     description="Learn about Wise International Consultancy..."
 *     keywords="about, education consultancy, nepal"
 *   />
 * 
 *   // For blog/university detail pages with dynamic images:
 *   <SEOHead 
 *     title={blog.title}
 *     description={blog.excerpt}
 *     image={blog.featuredImage}
 *     type="article"
 *     schema={articleSchema}
 *   />
 */
const SEOHead = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  noindex = false,
  schema,
  canonicalUrl,
  publishedTime,
  modifiedTime,
  author,
}) => {
  const location = useLocation();

  // Build full title: "Page Title | Site Name" or just "Site Name" for homepage
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} - Study Abroad from Nepal | MBBS, Engineering, Management`;

  const metaDescription = description || DEFAULT_DESCRIPTION;
  const metaImage = image || DEFAULT_IMAGE;
  const canonical = canonicalUrl || `${SITE_URL}${location.pathname}`;

  // Clean description for meta (strip HTML, limit to 160 chars)
  const cleanDescription = metaDescription
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 160);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={cleanDescription} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />

      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={cleanDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Article specific OG tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={cleanDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />

      {/* Structured Data (JSON-LD) */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// ═══════════════════════════════════════════
// Pre-built Schema Generators for common pages
// ═══════════════════════════════════════════

export const createBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  })),
});

export const createArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  image: article.image,
  datePublished: article.publishedAt,
  dateModified: article.updatedAt || article.publishedAt,
  author: {
    '@type': 'Person',
    name: article.author,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/images/logo.png`,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/blog/${article.slug}`,
  },
});

export const createCourseSchema = (course) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: course.name,
  description: course.description,
  provider: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
  ...(course.duration && {
    timeRequired: course.duration,
  }),
  ...(course.category && {
    courseCategory: course.category,
  }),
});

export const createUniversitySchema = (university) => ({
  '@context': 'https://schema.org',
  '@type': 'CollegeOrUniversity',
  name: university.name,
  description: university.description,
  image: university.logo || university.images?.[0],
  address: {
    '@type': 'PostalAddress',
    addressLocality: university.city,
    addressCountry: university.country,
  },
  ...(university.contactInfo?.website && {
    url: university.contactInfo.website,
  }),
  ...(university.type && {
    additionalType: university.type,
  }),
});

export const createFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const createEventSchema = (event) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: event.title,
  description: event.description,
  startDate: event.startDate,
  endDate: event.endDate || event.startDate,
  image: event.image,
  organizer: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
  ...(event.location && {
    location: {
      '@type': 'Place',
      name: event.location,
    },
  }),
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: event.isOnline
    ? 'https://schema.org/OnlineEventAttendanceMode'
    : 'https://schema.org/OfflineEventAttendanceMode',
});

export const createLocalBusinessSchema = (settings) => ({
  '@context': 'https://schema.org',
  '@type': 'EducationalOrganization',
  name: settings?.siteName || SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/images/logo.png`,
  image: `${SITE_URL}/images/og-image.jpg`,
  description: settings?.siteDescription || DEFAULT_DESCRIPTION,
  address: {
    '@type': 'PostalAddress',
    streetAddress: settings?.address || '',
    addressLocality: 'Kathmandu',
    addressRegion: 'Bagmati',
    addressCountry: 'NP',
  },
  ...(settings?.phone && {
    telephone: settings.phone,
  }),
  ...(settings?.email && {
    email: settings.email,
  }),
  ...(settings?.googleMapsUrl && {
    hasMap: settings.googleMapsUrl,
  }),
  priceRange: '$$',
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '09:00',
    closes: '17:00',
  },
  areaServed: {
    '@type': 'Country',
    name: 'Nepal',
  },
});

export const SITE_NAME_CONST = SITE_NAME;
export const SITE_URL_CONST = SITE_URL;

export default SEOHead;